import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCheck, faCopy, faGift } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetcher } from '~/helpers/fetch';

type ReferralData = {
  referralCode: string;
  referralUrl: string;
};

type ReferralState = {
  isLoading: boolean;
  error: string | null;
  data: ReferralData | null;
};

const isReferralData = (payload: unknown): payload is ReferralData => {
  if (!payload || typeof payload !== 'object') return false;
  const maybe = payload as Record<string, unknown>;
  return (
    typeof maybe.referralCode === 'string' &&
    typeof maybe.referralUrl === 'string'
  );
};

const giftIcon = faGift as IconProp;
const copyIcon = faCopy as IconProp;
const checkIcon = faCheck as IconProp;

/**
 * Escapes HTML special characters to prevent XSS attacks
 * Converts <, >, &, ", ' to their HTML entity equivalents
 */
const escapeHtml = (text: string): string => {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
  };
  return text.replace(/[&<>"']/g, (char) => map[char]);
};

/**
 * Validates and escapes a URL for safe use in HTML href attributes
 * Returns the escaped URL if valid, or an empty string if invalid
 * Only allows http: and https: protocols to prevent javascript: and other unsafe schemes
 */
const escapeUrl = (url: string): string => {
  if (!url || typeof url !== 'string') {
    return '';
  }

  try {
    const u = new URL(url);
    // Only allow clickable http(s) links in generated HTML
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
    return escapeHtml(u.toString());
  } catch {
    // If URL parsing fails, return empty string to prevent unsafe links
    return '';
  }
};

export const Referrals = () => {
  const { t } = useTranslation();
  const [state, setState] = useState<ReferralState>({
    isLoading: true,
    error: null,
    data: null,
  });
  const [copyState, setCopyState] = useState<{
    status: 'idle' | 'success' | 'error';
    message: string | null;
  }>({ status: 'idle', message: null });

  // useEffect: fetch referral data once on mount; dependencies intentionally empty
  useEffect(() => {
    const fetchReferral = async () => {
      try {
        const data = await fetcher<ReferralData>('/api/referral');
        if (!isReferralData(data)) {
          setState({
            isLoading: false,
            error:
              "We couldn't retrieve your referral code. Please try again later.",
            data: null,
          });
          return;
        }
        setState({ isLoading: false, error: null, data });
      } catch {
        setState({
          isLoading: false,
          error:
            "We couldn't retrieve your referral code. Please try again later.",
          data: null,
        });
      }
    };

    fetchReferral();
  }, []);

  /**
   * Generates a formatted referral message with styling and blurb
   * Includes brand name (translatable), benefit description, and referral URL
   * Returns both plain text and HTML versions for rich clipboard support
   * All interpolated values are properly escaped to prevent XSS attacks
   */
  const generateReferralMessage = useCallback(
    (url: string): { text: string; html: string } => {
      const brandName = t('brandName', 'PropFit');

      // Normalize URL once: validate and only allow http(s) protocols
      // This ensures both plain text and HTML use the same validated URL
      let safeUrl = '';
      try {
        const u = new URL(url);
        if (u.protocol === 'http:' || u.protocol === 'https:') {
          safeUrl = u.toString();
        }
      } catch {
        // Invalid URL, safeUrl remains empty
      }

      // Escape values for safe HTML interpolation
      const escapedBrandName = escapeHtml(brandName);
      const escapedUrl = safeUrl ? escapeHtml(safeUrl) : '';

      // Localized strings for HTML version
      const discountBlurb = t(
        'referralDiscountBlurb',
        "We'll both get 50% off for 3 months when you finish your trial."
      );
      const ctaButtonText = t('referralCtaButton', '👉 Sign Up Now');

      // Escape localized strings for safe HTML interpolation
      const escapedCtaButtonText = escapeHtml(ctaButtonText);

      // Construct discount blurb HTML with strong tag for the discount portion
      // Find and wrap "50% off for 3 months" in strong tags before escaping
      const discountText = '50% off for 3 months';
      const discountIndex = discountBlurb.indexOf(discountText);
      const escapedDiscountBlurbHtml =
        discountIndex !== -1
          ? escapeHtml(discountBlurb.substring(0, discountIndex)) +
            '<strong style="color: #667eea;">' +
            escapeHtml(discountText) +
            '</strong>' +
            escapeHtml(
              discountBlurb.substring(discountIndex + discountText.length)
            )
          : escapeHtml(discountBlurb);

      // Plain text version - use safeUrl to ensure consistency with HTML
      const text = t(
        'referralCopyMessage',
        `Join me on {{brandName}}!

We'll both get 50% off for 3 months when you finish your trial!

👉 Sign up here: {{url}}

{{brandName}} helps you manage your assets more efficiently. Let's save together!`,
        { brandName, url: safeUrl }
      );

      // HTML version with styling
      // All interpolated values (escapedBrandName, escapedUrl, escapedDiscountBlurbHtml, escapedCtaButtonText) are properly escaped
      // CTA block is conditionally rendered only when escapedUrl is truthy to avoid empty/invalid links
      const ctaBlock = escapedUrl
        ? `
            <div style="text-align: center; margin: 32px 0;">
              <a href="${escapedUrl}" style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 14px 32px; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 16px; box-shadow: 0 4px 6px rgba(102, 126, 234, 0.25);">
                ${escapedCtaButtonText}
              </a>
            </div>
          `
        : '';

      const html = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; max-width: 600px; line-height: 1.6; color: #333;">
          <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 24px; border-radius: 12px 12px 0 0; text-align: center;">
            <h2 style="margin: 0; color: white; font-size: 24px; font-weight: 600;">Join me on ${escapedBrandName}!</h2>
          </div>
          
          <div style="background: #ffffff; padding: 32px 24px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 12px 12px;">
            <div style="background: #f3f4f6; border-left: 4px solid #667eea; padding: 16px 20px; margin-bottom: 24px; border-radius: 4px;">
              <p style="margin: 0; font-size: 16px; color: #1f2937; font-weight: 500;">
                ${escapedDiscountBlurbHtml}
              </p>
            </div>
            ${ctaBlock}
            <div style="background: #fef3c7; border-radius: 8px; padding: 16px; margin-top: 24px;">
              <p style="margin: 0; font-size: 14px; color: #92400e; text-align: center;">
                <strong>${escapedBrandName}</strong> helps you manage your assets more efficiently. Let's save together!
              </p>
            </div>
          </div>
        </div>
      `;

      return { text, html };
    },
    [t]
  );

  // useCallback: memoize to avoid recreating handler on each render; depends on url and translation
  const handleCopyLink = useCallback(async () => {
    if (!state.data?.referralUrl) return;

    try {
      const { text, html } = generateReferralMessage(state.data.referralUrl);

      // Try to copy both HTML and plain text formats
      // Modern browsers support ClipboardItem for rich content
      // Defensive checks: ensure window exists, ClipboardItem is available, and navigator.clipboard is accessible
      if (
        typeof window !== 'undefined' &&
        typeof window.ClipboardItem !== 'undefined' &&
        navigator?.clipboard
      ) {
        const htmlBlob = new Blob([html], { type: 'text/html' });
        const textBlob = new Blob([text], { type: 'text/plain' });
        const clipboardItem = new window.ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob,
        });
        await navigator.clipboard.write([clipboardItem]);
      } else if (navigator?.clipboard?.writeText) {
        // Fallback to plain text for older browsers or partial clipboard API support
        await navigator.clipboard.writeText(text);
      } else {
        // Final fallback: throw error if clipboard API is completely unavailable
        throw new Error('Clipboard API not available');
      }

      setCopyState({ status: 'success', message: null });
    } catch {
      setCopyState({
        status: 'error',
        message: 'Unable to copy the referral link. Please try again.',
      });
    }
  }, [state.data?.referralUrl, generateReferralMessage]);

  if (state.isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='h-6 w-6 animate-spin rounded-full border-2 border-brand border-t-transparent' />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-6'>
      {/* Header */}
      <div className='flex items-center gap-3'>
        <div className='flex h-10 w-10 items-center justify-center rounded-full bg-shade-10'>
          <FontAwesomeIcon
            icon={giftIcon}
            className='h-5 w-5 text-primary'
            aria-hidden='true'
          />
        </div>
        <div>
          <h2 className='text-lg font-semibold text-primary'>
            Share PropFit. Save together.
          </h2>
          <p className='text-sm text-tertiary'>
            Refer a friend and both enjoy 50% off for 3 months.
          </p>
        </div>
      </div>

      {/* How it works */}
      <div className='rounded-lg border border-outline-variant bg-white p-4'>
        <h3 className='mb-3 font-semibold text-primary'>How it works</h3>
        <ol className='list-inside list-decimal space-y-2 text-sm text-secondary'>
          <li>Share your referral link or code with a friend.</li>
          <li>They sign up using your link or enter your code.</li>
          <li>
            When they finish their trial, you both get 50% off your subscription
            for 3 months!
          </li>
        </ol>
      </div>

      {/* Referral Link Section */}
      <div className='rounded-lg border border-outline-variant bg-white p-4'>
        <label className='mb-2 block text-sm font-medium text-secondary'>
          Your referral link
        </label>
        <div className='flex items-center gap-2'>
          <div
            className='flex-1 truncate rounded-md border border-outline-variant bg-white px-4 py-3 text-sm text-primary'
            aria-disabled={!state.data?.referralUrl}
          >
            {state.data?.referralUrl ?? '—'}
          </div>
          <button
            type='button'
            onClick={handleCopyLink}
            disabled={!state.data?.referralUrl}
            className='hover:bg-surface-container flex h-[46px] w-[46px] items-center justify-center rounded-md border border-outline-variant bg-white text-secondary transition-colors hover:text-primary disabled:cursor-not-allowed disabled:opacity-50'
            aria-label='Copy referral link'
            tabIndex={0}
          >
            <FontAwesomeIcon
              icon={copyState.status === 'success' ? checkIcon : copyIcon}
              className='h-5 w-5'
            />
          </button>
        </div>
        {state.error ? (
          <p className='mt-2 text-sm text-red-600' role='alert'>
            {state.error}
          </p>
        ) : null}
        {copyState.status === 'error' && copyState.message ? (
          <p className='mt-2 text-sm text-red-600'>{copyState.message}</p>
        ) : null}
      </div>

      {/* Policy link */}
      <a
        href='https://prop.fit/referral-policy'
        target='_blank'
        rel='noopener noreferrer'
        className='hover:text-brand/80 text-center text-sm text-brand underline'
        tabIndex={0}
        aria-label='Read the referral program policy'
      >
        Read the referral program policy
      </a>
    </div>
  );
};
